import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';

import { AUTH_ROUTE, HOME_ROUTE } from '@/constants/routes';
import { resolveAuthLanding } from '@/utils/authUtils';
import { RacoreText } from '@/components/Logos';
import { useTranslation } from '@/utils/i18n';

/**
 * Splash / auth-entry screen.
 *
 * Mirrors the legacy `app/index.tsx`: shows the branded splash briefly, then
 * routes to `/home` if a bearer token is stored, otherwise to `/auth/login`.
 */
export const Route = createFileRoute('/')({
  component: SplashScreen,
});

const SPLASH_MIN_MS = 1100;

function SplashScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const [landing] = await Promise.all([
        resolveAuthLanding({ auth: AUTH_ROUTE, home: HOME_ROUTE }),
        new Promise((resolve) => setTimeout(resolve, SPLASH_MIN_MS)),
      ]);
      if (!cancelled) navigate({ to: landing, replace: true });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      className="tw:relative tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center"
      data-testid="screen-splash"
    >
      <motion.div
        className="tw:flex tw:flex-col tw:items-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <img
          src="/images/logo/Icon.png"
          alt=""
          aria-hidden="true"
          className="tw:mb-5 tw:size-[70px] tw:object-contain"
        />
        <RacoreText width={186} height={38} />
        <p className="tw:mt-3 tw:caption tw:italic tw:text-white/80">{t('login.slogan')}</p>
      </motion.div>
    </div>
  );
}
